/*
  Size guide lightbox — opens size card images in the theme's PhotoSwipe.

  Reuses the exact engine and UI of the product gallery zoom: the
  "vendor" and "photoswipe" modules from the theme's import map (cached
  if the gallery already loaded them) and the same close/prev/next
  button markup. Nothing is imported until the first click.
*/
(() => {
  class SizeGuideGallery extends HTMLElement {
    #abortController;
    #lightbox;

    connectedCallback() {
      this.#abortController?.abort();
      this.#abortController = new AbortController();

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.size-guide__media-button');

        if (button && this.contains(button)) {
          this.#open(button);
        }
      }, { signal: this.#abortController.signal });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#lightbox?.destroy();
      this.#lightbox = undefined;
    }

    get #images() {
      return Array.from(this.querySelectorAll('.size-guide__media-button img'));
    }

    async #open(button) {
      const images = this.#images;
      const index = Math.max(images.indexOf(button.querySelector('img')), 0);

      const dataSource = images.map((image) => ({
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute('width')),
        height: parseInt(image.getAttribute('height')),
        alt: image.alt,
        thumbCropped: true
      }));

      (await this.#getLightbox()).loadAndOpen(index, dataSource);
    }

    /* Created on demand, mirroring the product gallery's configuration */
    async #getLightbox() {
      if (this.#lightbox) {
        return this.#lightbox;
      }

      const { PhotoSwipeLightbox } = await import('vendor');
      const strings = window.themeVariables.strings;

      this.#lightbox = new PhotoSwipeLightbox({
        pswpModule: () => import('photoswipe'),
        bgOpacity: 1,
        maxZoomLevel: 3,
        closeTitle: strings.closeGallery,
        zoomTitle: strings.zoomGallery,
        errorMsg: strings.errorGallery,
        arrowPrev: false,
        arrowNext: false,
        counter: false,
        close: false,
        zoom: false
      });

      this.#lightbox.on('afterInit', () => {
        const dialog = this.#lightbox.pswp.element;

        dialog.setAttribute('aria-label', strings.zoomGallery);
        dialog.setAttribute('aria-modal', 'true');
      });
      this.#lightbox.on('uiRegister', () => this.#registerUi());
      this.#lightbox.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);
      this.#lightbox.init();

      return this.#lightbox;
    }

    /* Same buttons as the product gallery lightbox */
    #registerUi() {
      const pswp = this.#lightbox.pswp;
      const strings = window.themeVariables.strings;

      pswp.ui.registerElement({
        name: 'close-button',
        className: 'circle-button circle-button--xl hover:animate-icon-block',
        ariaLabel: strings.closeGallery,
        order: 2,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
            <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
          </svg>
        `,
        onClick: () => pswp.close()
      });

      if (pswp.options.dataSource.length > 1) {
        pswp.ui.registerElement({
          name: 'previous-button',
          className: 'circle-button hover:animate-icon-inline',
          ariaLabel: strings.previous,
          order: 1,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
              <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => pswp.prev()
        });

        pswp.ui.registerElement({
          name: 'next-button',
          className: 'circle-button hover:animate-icon-inline',
          ariaLabel: strings.next,
          order: 3,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
              <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => pswp.next()
        });
      }
    }
  }

  if (!window.customElements.get('size-guide-gallery')) {
    window.customElements.define('size-guide-gallery', SizeGuideGallery);
  }
})();
